import React, {ReactElement, useEffect, ReactNode, useState} from 'react';
import {BrowserRouter, useLocation} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {ErrorBoundary} from 'react-error-boundary';
import {useIsAuthenticated} from '@azure/msal-react';

import {postUIErrorLog} from '@shared/services/service-handlers/PrivateService';
import InitSharedRepo from '../../shared';
import {useAuthContext} from './context/AuthContext';
import Home from './modules/home/main/container/Home';
import AuthUtils from './utils/AuthUtils';

import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {LoadingScreen, SplashScreen} from './minimal-components/loading-screen';
import PublicHome from '@modules/home/main/container/PublicHome';
import EncryptUtils from './utils/EncryptUtils';
import ProgressBar from './minimal-components/progress-bar/progress-bar';
import {ErrorFound} from './sections/error';
import CompactLayout from './layouts/compact';
import {fetchEnvironmentConfig} from './utils/helperUtils';
import {BASE_ROUTE} from './routes/BaseRoute';
import {PUBLIC_ROUTES} from './routes/RouteConstants';

const athenaRoutes = [
  `/${BASE_ROUTE}/${PUBLIC_ROUTES.ATHENA}`,
  `/${BASE_ROUTE}/${PUBLIC_ROUTES.ATHENA_EXTERNAL}`
];

const AppInitializer = (): ReactElement => {
  const {authState, login} = useAuthContext();
  const {isAuthenticated} = authState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isAzureAuthenticated = useIsAuthenticated();
  const {enqueueSnackbar} = useSnackbar();
  const [isAppReady, setAppReady] = useState(false);

  const attachAppListeners = (): void => {
    // Any app listeners to be added
  };
  const removeAppListeners = (): void => {
    // App app listeners to be removed
  };

  useEffect(() => {
    async function onMount() {
      // Need to remove this line, temporary
      if (window.location.pathname !== '/bytepad/lmsLanding') {
        const token = AuthUtils.getAuthToken();
        if (token?.length) {
          login({token});
          InitSharedRepo.initAfterAuth({token});
          AuthUtils.clearSelectedLoginType();
        }
      }
      await fetchEnvironmentConfig();
      setTimeout(() => {
        setAppReady(true);
      }, 100);
      attachAppListeners();
    }
    onMount();

    return () => {
      removeAppListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = (error: any): void => {
    AuthUtils.clearStorage({skip: ['authConfig']});
    if (typeof error?.resetErrorBoundary === 'function') {
      error?.resetErrorBoundary();
    }
  };

  const renderRoutes = (): ReactNode => {
    // return <SplashScreen />;
    if (!isAppReady) {
      return <SplashScreen />;
    } else if (window.location.pathname === '/bytepad/lmsLanding') {
      return <PublicHome />;
    }
    // Render the private routes
    // else if (isAzureAuthenticated || isAuthenticated) {
    else if (
      isAuthenticated &&
      !athenaRoutes.includes(window?.location?.pathname)
    ) {
      return <Home />;
    }
    return <PublicHome />;
  };

  return (
    <BrowserRouter>
      <ErrorBoundary
        FallbackComponent={error => (
          <CompactLayout>
            <ErrorFound error={error} handleReset={handleReset} />
          </CompactLayout>
        )}
        onError={async (error, info) => {
          try {
            const data = {
              error: error?.message || '',
              error_description: info?.componentStack || ''
            };
            const encryptRequestData = EncryptUtils.encryptedData(data);

            await postUIErrorLog({
              tk: encryptRequestData
            });
          } catch (e) {
            const errMessage = getErrorMessage(e);
            enqueueSnackbar(errMessage, SnackBarConfig.getError());
          }
        }}
        onReset={() => {
          window?.location?.reload();
        }}>
        <ProgressBar />
        <React.Suspense fallback={<LoadingScreen />}>
          {renderRoutes()}
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
export default AppInitializer;
